import { Module, Mutation, Action } from "vuex-module-decorators";
import { IntegralStatusEnums } from "@/enums/growth";
import PaginationModule from "@/store/base-modules/PaginationModule";
import { RootState } from "@/store";
import { queryRequest, handlePaginationData } from "@/utils/request";
import {
  queryIntegralFlow,
  integralMonth,
  getUserIntegral,
  getReviewIntegral
} from "@/graphql/star-level.gql";
import { queryDataDictionaryListLang } from "@/graphql/dictionary.gql";
import { formatByDate, getDateBefore36 } from "@/utils/common/date";
import type { PaginationDataState } from "@/types";

export interface IntegralDetailState extends PaginationDataState {
  statusTab: number;
  searchCondition: any;
}

@Module({ name: "IntegralDetailModule", namespaced: true })
export default class IntegralDetailModule
  extends PaginationModule<IntegralDetailState, RootState>
  implements IntegralDetailState {
  public listData: any[] = [];
  public statusTab: number = IntegralStatusEnums.CHECKED; // 头部状态选择
  public searchCondition: any = { _type: "" }; // 筛选条件
  public openFilterFlag: boolean = false; // 打开筛选模块
  public timeSumMap = {}; // 时间统计集合
  public filterData: any = []; // 筛选条件数据
  public userIntegral: any = {}; // 用户积分

  @Mutation
  setFilterDataMutation(data: any[]) {
    this.filterData = data || [];
  }
  @Mutation
  setUserIntegralMutation(data: any) {
    this.userIntegral = data || {};
  }
  @Mutation
  saveListDataMutation(data: any[]) {
    this.listData = data || [];
  }
  @Mutation
  addListDataMutation(data: any[] = []) {
    this.listData.push(...(data || []));
  }
  @Mutation
  setStatusTabMutation(data: number) {
    this.statusTab = data ?? IntegralStatusEnums.CHECKED;
  }
  @Mutation
  setSearchConditionMutation({ key = "", value = "" } = {}) {
    if (!key && !value) {
      this.searchCondition = { _type: "" };
    } else {
      this.searchCondition[key] = value;
    }
  }
  @Mutation
  setOpenFilterFlagMutation(data: boolean) {
    this.openFilterFlag = data ?? false;
  }
  @Mutation
  setTimeSumMapMutation({ key = "", value = "" } = {}) {
    if (!(key && value)) {
      this.timeSumMap = {};
    } else {
      this.timeSumMap[key] = value;
    }
  }

  @Action
  async queryListAction({ callback }) {
    const {
      state: {
        statusTab,
        searchCondition,
        paginationData,
        paginationData: { pageNum, pageSize }
      },
      commit
    } = this.context;
    const [start, end] = getDateBefore36();
    let { data = [], totalCount } = handlePaginationData(
      await queryRequest({
        query: queryIntegralFlow,
        variables: {
          state: statusTab,
          startDate: searchCondition.startTime || formatByDate(start),
          endDate: searchCondition.endTime || formatByDate(end),
          type: searchCondition._type,
          pageInput: { pageNum, pageSize }
        }
      })
    );
    data = await callback(data);
    commit(
      pageNum === 1 ? "saveListDataMutation" : "addListDataMutation",
      data
    );
    // 保存总数
    commit("savePaginationData", { ...paginationData, totalCount });
  }

  @Action
  async queryFilterDataAction() {
    const data = await queryRequest({
      query: queryDataDictionaryListLang,
      variables: {
        input: {
          ddCode: "INTEGRAL_SOURCE", // 积分筛选
          lang: window.i18n.locale
        }
      }
    });
    this.context.commit("setFilterDataMutation", data);
  }
  @Action
  async getUserIntegralAction() {
    const data = await queryRequest({
      query: getUserIntegral
    });
    const { code } = await queryRequest({
      query: getReviewIntegral
    });
    data.waitCheckScore = code;
    this.context.commit("setUserIntegralMutation", data);
  }

  @Action
  async queryIntegralMonthAction(variables: Object) {
    const data = await queryRequest({
      query: integralMonth,
      variables
    });
    data.forEach(o => {
      this.context.commit("setTimeSumMapMutation", {
        key: o.year + String(o.month).padStart(2, "0"),
        value: o
      });
    });
  }
}
